import PageHero from "@/components/base/PageHero";
import ArrowLink from "@/components/base/ArrowLink";
import SectionLabel from "@/components/base/SectionLabel";
import CtaBand from "@/components/feature/CtaBand";
import ProductBlock from "@/pages/produkte/components/ProductBlock";
import StickyAnchors from "@/pages/produkte/components/StickyAnchors";
import {
  brandAssets,
  businessFields,
  capabilities,
  downloads,
  pitchProducts,
  silverModels,
} from "@/mocks/site";

const [machines, pitch, charging] = businessFields;

export default function Produkte() {
  return (
    <>
      <PageHero
        label="Produkte"
        title="Qualität und Individualität — made in Germany"
        lead="Wir sind ein facettenreiches Unternehmen: Sondermaschinen und Vorrichtungen, Ausstattung für Stell- und Campingplätze sowie Ladestationen für Elektrofahrzeuge."
      />
      <StickyAnchors />

      <div className="bg-background-50">
        <div className="mx-auto w-full max-w-shell px-4 md:px-6">
          <ProductBlock
            id={machines.id}
            index={machines.index}
            title={machines.title}
            lead={machines.lead}
            body={machines.body}
            bullets={machines.bullets}
            image={machines.image}
            imageAlt={machines.imageAlt}
          >
            <div className="mt-8 border border-foreground-200 bg-background-100 p-4 md:p-5">
              <p className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-500">
                Fertigung im Haus
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                {capabilities.slice(0, 6).map((item) => (
                  <span
                    key={item.title}
                    className="border border-foreground-200 bg-background-50 px-2.5 py-1.5 font-label text-[10.5px] uppercase tracking-[0.08em] text-foreground-700"
                  >
                    {item.title}
                  </span>
                ))}
              </div>
              <ArrowLink to="/unternehmen#fertigung" className="mt-4">
                Maschinenpark ansehen
              </ArrowLink>
            </div>
          </ProductBlock>

          <ProductBlock
            id={pitch.id}
            index={pitch.index}
            title={pitch.title}
            lead={pitch.lead}
            body={pitch.body}
            bullets={pitch.bullets}
            image={pitch.image}
            imageAlt={pitch.imageAlt}
            reversed
          >
            <div className="mt-8">
              <p className="font-label text-[10px] uppercase tracking-[0.2em] text-foreground-500">
                Ausführungen der Silver-Serie
              </p>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {silverModels.map((model) => (
                  <div
                    key={model.name}
                    className="border border-foreground-200 bg-background-100 p-4 transition-colors hover:border-foreground-950/25"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                      <span className="font-heading text-[15px] font-bold text-foreground-950">
                        {model.name}
                      </span>
                      <span className="font-label text-[9px] uppercase tracking-[0.12em] text-foreground-400">
                        {model.note}
                      </span>
                    </div>
                    <p className="mt-2 text-[12px] leading-relaxed text-foreground-600">
                      {model.text}
                    </p>
                    <p className="mt-2 font-label text-[10px] uppercase tracking-[0.08em] text-foreground-400">
                      {model.specs}
                    </p>
                  </div>
                ))}
              </div>

              <p className="mt-7 font-label text-[10px] uppercase tracking-[0.2em] text-foreground-500">
                Ergänzende Komponenten
              </p>
              <div className="mt-2 border-t border-foreground-200">
                {pitchProducts.map((product) => (
                  <div
                    key={product.name}
                    className="flex flex-col gap-1 border-b border-foreground-200 py-3 sm:flex-row sm:items-baseline sm:gap-6"
                  >
                    <span className="w-full flex-none font-heading text-[13.5px] font-bold text-foreground-950 sm:w-56">
                      {product.name}
                    </span>
                    <span className="text-[12.5px] leading-relaxed text-foreground-600">
                      {product.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </ProductBlock>

          <ProductBlock
            id={charging.id}
            index={charging.index}
            title={charging.title}
            lead={charging.lead}
            body={charging.body}
            bullets={charging.bullets}
            image={charging.image}
            imageAlt={charging.imageAlt}
          >
            <div className="mt-8 flex flex-col gap-5 border border-foreground-200 bg-background-100 p-4 sm:flex-row sm:items-center md:p-5">
              <div className="h-[70px] w-[150px] flex-none">
                <img
                  src={brandAssets.eMobilityMark}
                  alt="Ladestationen für Elektrofahrzeuge"
                  width={173}
                  height={83}
                  className="h-full w-full object-contain object-left"
                />
              </div>
              <div className="flex-1">
                <p className="font-heading text-[14.5px] font-bold text-foreground-950">
                  Auch für E-Bikes und Pedelecs
                </p>
                <p className="mt-1 text-[12.5px] leading-relaxed text-foreground-600">
                  Ladestationen für Zweiräder — für Stellplätze, Betriebe und öffentliche Flächen.
                  Sprechen Sie uns auf die passende Ausführung an.
                </p>
              </div>
              <ArrowLink to="/kontakt">Anfragen</ArrowLink>
            </div>
          </ProductBlock>
        </div>
      </div>

      <section className="bg-background-100 py-12 md:py-16">
        <div className="mx-auto flex w-full max-w-shell flex-col gap-6 px-4 md:flex-row md:items-center md:justify-between md:px-6">
          <div>
            <SectionLabel className="mb-4">Infomaterial</SectionLabel>
            <h2 className="font-heading text-[22px] font-extrabold tracking-tightest text-foreground-950 md:text-[28px]">
              Broschüre Campingprodukte
            </h2>
            <p className="mt-2 max-w-md text-[13.5px] leading-relaxed text-foreground-600">
              Alle Anlagen der Silver-Serie sowie Strom- und Wassersäulen im Überblick — als PDF.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            {downloads.map((item) => (
              <a
                key={item.href}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 whitespace-nowrap border border-foreground-950 bg-foreground-950 px-5 py-4 font-heading text-[11.5px] font-bold uppercase tracking-[0.13em] text-background-50 transition-colors hover:bg-primary-600 hover:border-primary-600"
              >
                <i className="ri-file-pdf-2-line text-[16px]" />
                {item.label}
                <i className="ri-arrow-right-up-line text-[14px] transition-transform group-hover:translate-x-0.5" />
              </a>
            ))}
            <a
              href="tel:+4991971404"
              className="inline-flex items-center gap-2 whitespace-nowrap border border-foreground-300 px-5 py-4 font-heading text-[11.5px] font-bold uppercase tracking-[0.13em] text-foreground-900 transition-colors hover:border-foreground-950"
            >
              <i className="ri-phone-line text-[15px]" />
              Beratung
            </a>
          </div>
        </div>
      </section>

      <CtaBand
        title="Welche Lösung passt zu Ihrem Platz?"
        text="Sagen Sie uns, wie viele Stellplätze Sie ausstatten möchten und welche Anschlüsse gebraucht werden — wir schlagen die passende Ausführung vor."
      />
    </>
  );
}